import { UIIcon } from '../../../config/icons';
import { sUploadImage, YProgressCallback } from '../../mdl/s-upload';
import { $cdn } from '../../y/cdn';
import { $log } from '../../y/debug';
import { $trigger, YBlurEvent, YEventHandlers, YFocusEvent, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { $form, YInput, YInputOptions } from '../../y/form';
import { $i18n } from '../../y/i18n';
import { YId } from '../../y/types';
import { uiAnimate, uiScrollTo } from '../animate';
import { uiSetBG } from '../bg';
import { isImageMime, UIContent, UIDropDelegate, uiInitDropTarget } from '../dnd';
import { $el, UILink } from '../elements';
import { ui_new_image_mimetypes, uiMakeFakeUploadInput } from '../functions';
import { UIClassesSpec, UIImage, UIImageSize } from '../types';
import { UIView, UIViewSpec } from '../view';
import { UIButton } from './button';
import { UIProgressWheel } from './progress';

interface UIImageViewOptions extends YInputOptions {
    classes?: UIClassesSpec;
    link?: UILink;
    aspect?: number;
}

export class UIImageView extends UIView {
    constructor(image: UIImage, options: UIImageViewOptions = {}) {
        const classes = ['ui-image', options.classes];

        if(options.link) {
            super($el.A(options.link, undefined, classes));
        } else {
            super('div', classes);
        }

        if(options.aspect) {
            this.classList.add('u-aspect');
            this.root.style.setProperty('--ui-aspect', options.aspect.toFixed(3));
        }

        uiSetBG(this, image);
    }
}

interface UIImageInputOptions extends YInputOptions {
    classes?: UIClassesSpec;
    size?: UIImageSize;
    always_remove?: boolean;
    placeholder?: UIViewSpec;
    download?: boolean;
    scope: string;
    aspect?: number;
    new_accept?: boolean;
}

type UIImageInputCallback = (file: File, progress: YProgressCallback) => Promise<YId>;

export class UIImageInput extends UIView implements YInput<YId | undefined>, UIDropDelegate {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<YId | undefined> & YBlurEvent & YFocusEvent>;
    private _value?: YId;

    private _size?: UIImageSize;
    private _required?: boolean;

    buttons_view: HTMLElement;

    private _download?: UIButton;
    private _scope: string;
    private _progress: UIProgressWheel;
    private _disabled: boolean;

    okay: boolean;
    dirty!: boolean;

    on_remove?: () => void;
    on_image?: UIImageInputCallback;

    constructor(options: UIImageInputOptions) {
        super('div', ['ui-image-input ui-image', options.classes]);

        if(options.aspect)
            this.root.style.setProperty('aspect-ratio', options.aspect.toFixed(3));
        this._size = options.size;
        this._required = options.required;
        this._scope = options.scope;
        this._disabled = false;

        this.okay = true;

        uiInitDropTarget(this);

        let input = uiMakeFakeUploadInput(files => {
            this._onFile(files[0]);
        }, options.new_accept ? ui_new_image_mimetypes : undefined);

        this.ui_add([
            this.buttons_view = $el.DIV('r-buttons l-over', [
                $el.S(2),
                new UIButton({
                    kind: 'k-square', classes: 'm-round m-transparent', icon: UIIcon.UPLOAD, on_click: () => {
                        this.classList.remove('f-invalid');
                        input.click();
                    }
                }),
                $el.S(0.5),
                options.download && (this._download = new UIButton({ kind: 'k-square', classes: 'm-round m-transparent', icon: UIIcon.DOWNLOAD, link: '' })),
                $el.S(0.5),
                new UIButton({
                    kind: 'k-square', classes: 'm-round m-transparent', icon: UIIcon.XMARK, on_click: () => {
                        this.classList.remove('f-invalid');
                        if(this._value) {
                            $form.setDirty(this, true);
                            this.value = undefined;
                            $trigger(this, 'value', this._value);

                            if(options.always_remove) {
                                this.on_remove && this.on_remove();
                            }
                        } else {
                            this.on_remove && this.on_remove();
                        }
                    }
                }),
                $el.S(2)
            ]),
            $el.DIV('r-progress l-over', this._progress = new UIProgressWheel),
            $el.DIV('r-empty l-over', [
                options.placeholder || $el.ICON(UIIcon.IMAGE),
                $el.SPAN('r-text', $i18n.ui.img_drop)
            ])
        ]);

        if(this._download)
            this._download.ui_hide();

        this.value = undefined;
    }

    dropEnter(content: UIContent) {
        if(this._disabled) return false;

        this.classList.remove('f-invalid');
        return content.type === 'files';
    }

    drop(content: UIContent) {
        if(this._disabled) return;

        this.classList.remove('f-invalid');
        if(content.type === 'files') {
            const file = content.value![0] as File;
            if(!isImageMime(file.type)) return;
            this._onFile(file);
        }
    }

    private async _onFile(file: File) {
        $form.setDirty(this, true);
        this._applyValue(undefined);

        const progress: YProgressCallback = p => {
            this._progress.progress = p;
        }

        progress(0);
        this.classList.add('f-progress');

        let value: YId | undefined;

        try {
            value = this.on_image ? (await this.on_image(file, progress)) : (await sUploadImage(file, this._scope, {}, progress)).id;
        } catch(e) {
            $log('errors', 'error', e);
        }

        this._setValue(value, true, true);
        this.classList.remove('f-progress');
    }

    private _setValue(value: YId | undefined, notify: boolean, apply: boolean) {
        if(!value)
            value = undefined;
        this._value = value;
        $trigger(this, 'value', value);
        notify && $trigger(this, 'input');
        apply && this._applyValue(value);
        $form.setOkay(this, !(this._required && !value));
        if(this._download) {
            if(value) {
                this._download.link = { href: $cdn.media(value), download: value + '.jpg' };
                this._download.ui_show();
            } else {
                this._download.ui_hide();
            }
        }
    }

    private _applyValue(value: YId | undefined) {
        uiSetBG(this, { media: value, size: this._size });
        this.classList.toggle('f-empty', !value);
    }

    get value() {
        return this._value;
    }

    set value(value: YId | undefined) {
        this._setValue(value, false, true);
    }

    get disabled() {
        return this._disabled;
    }

    set disabled(disabled: boolean) {
        this._disabled = disabled;
        this.classList.toggle('f-disabled', disabled);
    }

    markInvalid(focus: boolean, force?: boolean): boolean {
        if(this.okay && !force) return false;
        if(focus)
            this.focus();
        uiAnimate([{ what: this, animation: 'flash-invalid', duration: 1.5 }]);
        this.classList.add('f-invalid');
        return focus;
    }

    focus() {
        uiScrollTo(this);
    }
}
